<template>
  <div class="local-body">
    <CosmicBackground />

    <!-- BACK -->
    <div class="back-btn">
      <v-btn @click="$emit('back')" color="white" icon x-large>
        <v-icon color="white" style="font-size: 48px">mdi-chevron-left</v-icon>
      </v-btn>
    </div>

    <!-- 재화 잔액 (🥕) -->
    <div class="balance-pill slide-left">🥕 {{ balance.toLocaleString() }}</div>

    <section class="gacha-section">
      <div class="responsive-center slide-left">
        <h1 class="title-cosmic">
          <span>{{ $t("onboarding.gachaTitlePrefix") }}</span>
          <span class="title-accent">{{ $t("brand.name") }}</span>
          <span>{{ $t("onboarding.gachaTitleSuffix") }}</span>
        </h1>
      </div>

      <!-- GACHA STAGE (matter.js 물리 머신) -->
      <div class="gacha-stage slide-left-1s">
        <GachaMachine ref="machine" />
      </div>
      <div class="reveal-name" :class="{ show: revealed }">{{ revealed ? drawnName : "" }}</div>

      <!-- ACTIONS -->
      <div class="gacha-actions">
        <template v-if="!revealed">
          <v-btn class="cosmic-cta gacha-btn" rounded :loading="isDrawing" :disabled="balance < cost" @click="draw">
            <span>{{ $t("onboarding.draw") }} &nbsp;·&nbsp; 🥕 {{ cost }}</span>
          </v-btn>
        </template>

        <template v-else>
          <v-btn
            class="reroll-btn gacha-btn"
            rounded
            outlined
            :loading="isDrawing"
            :disabled="balance < cost"
            @click="draw"
          >
            <span>{{ $t("onboarding.reroll") }} · 🥕 {{ cost }}</span>
          </v-btn>
          <v-btn class="cosmic-cta gacha-btn" rounded @click="confirm">
            <span>{{ $t("onboarding.startWithThis") }}</span>
          </v-btn>
        </template>
      </div>

      <!-- 전체보기(도감) -->
      <div class="d-flex justify-center mt-5">
        <v-btn text class="catalog-link" @click="showAll = true">
          <v-icon left small>mdi-grid</v-icon>{{ $t("onboarding.viewAll") }}
        </v-btn>
      </div>

      <p v-if="balance < cost && !isDrawing" class="low-balance">
        {{ $t("onboarding.lowBalance") }}
      </p>
    </section>

    <!-- 전체보기 다이얼로그 (도감 · 보기 전용) -->
    <v-dialog v-model="showAll" max-width="860" scrollable>
      <div class="catalog-dialog">
        <div class="catalog-dialog__head">
          <span class="catalog-dialog__title">{{ $t("onboarding.catalogTitle") }} ({{ modelMetas.length }})</span>
          <v-btn icon @click="showAll = false"><v-icon color="white">mdi-close</v-icon></v-btn>
        </div>
        <div class="catalog-dialog__body">
          <div class="d-flex flex-wrap justify-center">
            <div v-for="(meta, i) in modelMetas" :key="meta.modelIndex" class="px-2 py-2 catalog-item">
              <PreviewModelCard :index="i" :modelIndex="meta.modelIndex" :width="110" :height="138" />
            </div>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import PreviewModelCard from "../../components/PreviewModelCard.vue";
import CosmicBackground from "../../components/CosmicBackground.vue";
import GachaMachine from "../../components/GachaMachine.vue";
import { MODEL_METAS } from "../../constants";
import CurrencyApi from "@/api/CurrencyApi";

const DRAW_COST = 100; // 뽑기 1회 비용 (🥕)

export default {
  name: "OnboardingSelect",
  components: { PreviewModelCard, CosmicBackground, GachaMachine },
  props: {
    // 도감/풀 목록 — 기본값은 SSOT(MODEL_CATALOG) 파생 메타. 필요 시 부모가 주입 가능.
    modelMetas: {
      type: Array,
      default: () => MODEL_METAS,
    },
    selectedModelIndex: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      cost: DRAW_COST,
      drawn: null, // 현재 뽑힌 modelIndex
      revealed: false, // 결과 노출 여부
      isDrawing: false, // 뽑기 애니메이션 진행 중
      showAll: false, // 전체보기 다이얼로그
    };
  },
  computed: {
    // 실제 재화 잔액 (store SSOT). created에서 1회 로드.
    balance() {
      return this.$store.state.currencyBalance;
    },
    drawnName() {
      // 동물 언어팩($t)으로 현지화 — 로케일 전환 시 즉시 반영
      return this.drawn ? this.$t("animals." + this.drawn) : "";
    },
  },
  created() {
    // 실제 당근 잔액 로드 (mock 제거)
    this.$store.dispatch("loadCurrencyBalance");
  },
  methods: {
    async draw() {
      if (this.isDrawing || this.balance < this.cost) return;
      this.isDrawing = true;
      this.revealed = false; // 흔드는 동안 이름 숨김

      // 실제 차감: Edge Function(service_role) 경유 → 성공 시 신규 잔액으로 store 갱신.
      try {
        const newBalance = await CurrencyApi.spend(this.cost, "pet-gacha");
        this.$store.commit("__setCurrencyBalance", newBalance);
      } catch (e) {
        console.error("gacha spend failed", e);
        this.isDrawing = false;
        const title = (e && e.detail && e.detail.message) || this.$t("onboarding.lowBalance");
        if (this.$notify) this.$notify({ type: "warning", title, duration: 2400 });
        return; // 차감 실패 시 뽑지 않음
      }

      // 결과는 물리가 결정한다: 통을 흔든 뒤 '출구에 가장 가까운 공'이 떨어져 나온다.
      const machine = this.$refs.machine;
      const idx = machine && machine.spin ? await machine.spin() : null;

      this.drawn = idx || this.drawn;
      this.isDrawing = false;
      this.revealed = !!this.drawn;
    },
    confirm() {
      if (!this.drawn) return;
      this.$emit("select", this.drawn); // 부모 selectedModelIndex 갱신
      this.$emit("next"); // 메타 입력 단계로
    },
  },
};
</script>

<style scoped>
.local-body {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  padding-bottom: 32px;
  background-color: #0c0822;
}

.back-btn {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 3;
}

.balance-pill {
  position: absolute;
  top: 26px;
  right: 26px;
  z-index: 3;
  padding: 8px 18px;
  border-radius: 999px;
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif;
  font-size: 18px;
  color: #fff3d6;
  background: rgba(22, 16, 45, 0.6);
  border: 1px solid rgba(255, 196, 92, 0.4);
  box-shadow: 0 0 18px rgba(255, 170, 60, 0.25);
}

.gacha-section {
  position: relative;
  z-index: 1;
  padding-top: 64px;
}

.title-cosmic {
  text-align: center;
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif;
  font-weight: 400;
  font-size: clamp(30px, 5vw, 44px);
  color: #f3effc;
  text-shadow: 0 0 18px rgba(124, 92, 255, 0.45);
}
.title-cosmic .title-accent {
  color: #d9b3ff;
  text-shadow: 0 0 16px rgba(217, 179, 255, 0.7);
}

.responsive-center {
  width: 80%;
  margin: auto;
}

/* ===== GACHA STAGE ===== */
.gacha-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
}

/* 결과 이름 (트레이 안착 후 노출) */
.reveal-name {
  text-align: center;
  min-height: 40px;
  margin-top: 6px;
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif;
  font-size: 30px;
  color: #f3effc;
  text-shadow: 0 0 16px rgba(217, 179, 255, 0.7);
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.reveal-name.show {
  opacity: 1;
  transform: translateY(0);
}

/* ===== ACTIONS ===== */
.gacha-actions {
  display: flex;
  gap: 14px;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;
}
.reroll-btn {
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif !important;
  color: #d9c8ff !important;
  border-color: rgba(201, 184, 255, 0.5) !important;
}
.catalog-link {
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif !important;
  font-size: 17px !important;
  color: #b9aee0 !important;
  letter-spacing: 0.5px;
}
.low-balance {
  text-align: center;
  margin-top: 18px;
  color: #ffcf8f;
  font-size: 13px;
}

/* 공용 CTA */
.cosmic-cta {
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif !important;
  font-size: 21px !important;
  color: #fff !important;
  background: linear-gradient(135deg, #7c5cff 0%, #b15bff 45%, #ff6fae 100%) !important;
  box-shadow: 0 10px 28px rgba(124, 92, 255, 0.5) !important;
  letter-spacing: 0.5px;
}

/* 가챠 버튼 공통 사이즈(뽑기 = 다시뽑기 = 시작) — cosmic-cta/reroll보다 뒤에 두어 라벨 크기까지 통일 */
.gacha-btn {
  min-width: 280px;
  height: 66px !important;
  font-size: 23px !important;
  padding: 0 32px !important;
}

/* ===== 도감 다이얼로그 ===== */
.catalog-dialog {
  background: #140d2b;
  border: 1px solid rgba(149, 85, 253, 0.34);
  border-radius: 16px;
  overflow: hidden;
}
.catalog-dialog__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: rgba(22, 16, 45, 0.9);
  border-bottom: 1px solid rgba(149, 85, 253, 0.28);
}
.catalog-dialog__title {
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif;
  font-size: 22px;
  color: #f3effc;
}
.catalog-dialog__body {
  padding: 20px 16px;
  max-height: 70vh;
  overflow: auto;
}
.catalog-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ===== ANIMATIONS ===== */
.slide-left {
  animation: slideLeftText 0.8s ease-in-out;
}
@keyframes slideLeftText {
  0% {
    transform: translateX(30px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}
.slide-left-1s {
  animation: slideLeft 0.8s ease-in-out;
  animation-delay: 0.6s;
  animation-fill-mode: both;
}
@keyframes slideLeft {
  0% {
    transform: translateY(24px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-12px);
  }
}
@keyframes shake {
  0%,
  100% {
    transform: translate(0, 0) rotate(0);
  }
  20% {
    transform: translate(-8px, 4px) rotate(-7deg);
  }
  40% {
    transform: translate(8px, -4px) rotate(7deg);
  }
  60% {
    transform: translate(-6px, 4px) rotate(-5deg);
  }
  80% {
    transform: translate(6px, -2px) rotate(5deg);
  }
}
.pop-in {
  animation: popIn 0.5s cubic-bezier(0.18, 0.89, 0.32, 1.28);
}
@keyframes popIn {
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
@keyframes pulse {
  0%,
  100% {
    transform: translate(-50%, -60%) scale(0.9);
    opacity: 0.7;
  }
  50% {
    transform: translate(-50%, -60%) scale(1.1);
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .capsule,
  .reveal__halo,
  .slide-left,
  .slide-left-1s,
  .pop-in {
    animation: none !important;
  }
}
</style>
